import React, { useRef, useState } from "react";
import { Spinner, Modal, ModalBody, Button } from "reactstrap";
import Cropper from "react-easy-crop/umd/react-easy-crop.js";
import { toast } from "react-toastify";
import { updateImg } from "store/actions/authActions";

const ImageUploadCard = ({ user, uid, dispatch, imgLoading }) => {
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImageFile, setCroppedImageFile] = useState(null);

  const handleFileChange = (e) => {
    let file = e.target.files[0];
    if (!file) return;
    if (file.size <= 1000000) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setCropModalOpen(true);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("File size exceeds 1MB");
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/jpeg");
    });
  };

  const handleCropSave = async () => {
    if (selectedFile && imagePreview && croppedAreaPixels) {
      try {
        const croppedBlob = await getCroppedImg(imagePreview, croppedAreaPixels);
        
        const croppedFile = new File([croppedBlob], selectedFile.name, {
          type: "image/jpeg",
          lastModified: Date.now(),
        });

        setCroppedImageFile(croppedFile);
        
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(croppedFile);
        
        setCropModalOpen(false);
      } catch (err) {
        console.error("Failed to crop image:", err);
        toast.error("Failed to crop image. Please try again.");
      }
    }
  };

  const handleUpload = (e) => {
    e.preventDefault();
    if (!croppedImageFile) return;
    let obj = {
      image: croppedImageFile,
      fileName: user?.player_image?.file_name || "player_image",
    };
    dispatch(updateImg(obj, uid)).then(() => {
      setCroppedImageFile(null);
      setImagePreview(null);
      setSelectedFile(null);
    });
  };

  const triggerFileSelect = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="saas-card">
      <div className="card-header-saas">
        <h3>Profile Image</h3>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "16px" }}>
          <img
            src={imagePreview || user?.player_image?.url || "https://via.placeholder.com/150"}
            style={{ width: "90px", height: "90px", borderRadius: "12px", objectFit: "cover", border: "1px solid #cbd5e1" }}
            alt="Current avatar preview"
          />
          <div style={{ fontSize: "0.8125rem", color: "#64748b" }}>
            {croppedImageFile ? (
              <span className="text-success font-weight-bold">
                <i className="fas fa-check-circle mr-1"></i> Image ready for upload.
              </span>
            ) : (
              "Upload a high quality profile image. Format: JPEG, PNG. Max size: 1MB."
            )}
          </div>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          accept="image/png, image/gif, image/jpeg"
        />

        <div className="upload-zone" onClick={triggerFileSelect}>
          <i className="fas fa-cloud-upload-alt"></i>
          {selectedFile ? (
            <p>Selected: <span>{selectedFile.name}</span></p>
          ) : (
            <p>Drag & Drop or <span>Browse files</span></p>
          )}
        </div>

        <button
          className="saas-btn primary w-100"
          onClick={handleUpload}
          disabled={!croppedImageFile || imgLoading}
        >
          {imgLoading ? <Spinner size="sm" /> : "Update Image"}
        </button>
      </div>

      {/* Image Cropper Modal using react-easy-crop */}
      <Modal isOpen={cropModalOpen} toggle={() => setCropModalOpen(false)} centered size="md">
        <ModalBody>
          <h4 className="text-dark font-weight-bold mb-3">Adjust Profile Photo</h4>
          <p className="text-muted" style={{ fontSize: "0.8125rem" }}>
            Drag to pan the image or use the slider below to zoom.
          </p>
          
          {imagePreview && (
            <div className="position-relative mt-3" style={{ width: "100%", height: "320px", background: "#1e293b", borderRadius: "12px", overflow: "hidden" }}>
              <Cropper
                image={imagePreview}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
          )}

          <div className="mt-3 d-flex align-items-center gap-3">
            <span style={{ fontSize: "0.8125rem", color: "#475569", fontWeight: 600 }}>Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              style={{ flex: 1 }}
            />
          </div>
          
          <div className="text-right mt-4" style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <Button className="saas-btn secondary sm" onClick={() => setCropModalOpen(false)}>
              Cancel
            </Button>
            <Button className="saas-btn primary sm" onClick={handleCropSave}>
              Crop & Save
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ImageUploadCard;
